import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  classes,
  ErrorText,
  OutlinedInput,
  PrimaryButton,
  TransparentButton,
} from '@bloomberg/media-ui-core';
import RegUIClient, {
  CaptchaType,
  RegUIClientSocialLoginResponse,
} from 'reg-ui-client';

import { VerificationCodeInput } from '../EmailVerificationForm/VerificationCodeInput';
import { defaultMessages, LABEL_KEYS, useTranslateMessage } from '../i18n';
import { OrSeparator } from '../OrSeparator';
import { LoginRegistrationErrors } from '../utils/interfaces';

import {
  getGrecaptchaKey,
  getSkipRecaptchaParameter,
  useRecaptchaRequired,
} from './grecaptchaUtils';
import InvisibleRecaptcha from './InvisibleRecaptcha';

import styles from './styles.module.css';

export interface Props {
  thinking?: boolean;
  error?: LoginRegistrationErrors;
  email: string;
  theme?: 'light' | 'dark';
  codeReSent?: boolean;
  marketingConsent?: boolean;
  regUIClient?: RegUIClient;
  verificationCodeSent?: boolean;
  onEmailChange: () => void;
  onResendEmail: () => void;
  onPasswordLogin: (
    email: string,
    password: string,
    captcha: CaptchaType
  ) => Promise<void>;
  onVerificationCodeInputStarting: () => void;
  onVerificationCodeInputComplete: () => void;
  onVerificationCodeSubmit: (
    userDataResp: RegUIClientSocialLoginResponse
  ) => void;
}

export const EnterPasswordOrVerificationCode: React.FC<Props> = ({
  theme = 'light',
  thinking,
  email,
  error,
  codeReSent,
  marketingConsent,
  regUIClient,
  verificationCodeSent = true,
  onEmailChange,
  onResendEmail,
  onPasswordLogin,
  onVerificationCodeInputStarting,
  onVerificationCodeInputComplete,
  onVerificationCodeSubmit,
}) => {
  const translateMessage = useTranslateMessage();
  const passwordInputField = useRef<HTMLDivElement>(null);
  const recaptchaForm = useRef<HTMLFormElement>(null);

  const [passwordValue, setPasswordValue] = useState<string>('');
  const [passwordSubmitDisabled, setPasswordSubmitDisabled] =
    useState<boolean>(true);

  const [isMounted, setIsMounted] = useState(false);

  const recaptchaRequired = useRecaptchaRequired(isMounted, email, regUIClient);

  useEffect(
    () => setPasswordSubmitDisabled(!passwordValue.length),
    [passwordValue]
  );

  useEffect(() => {
    // The Sign In component is client side
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Auto-focus password field when verification code is disabled
    if (isMounted && !verificationCodeSent && passwordInputField.current) {
      const inputElement = passwordInputField.current.querySelector('input');

      inputElement?.focus();
    }
  }, [isMounted, verificationCodeSent]);

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget.value;

    setPasswordValue(value);
  };

  const submitPassword = async (
    captchaResponse: string | null
  ): Promise<void> => {
    const captcha: CaptchaType = {
      captchaResponse,
      useRecaptcha: recaptchaRequired,
      captchaBypass: getSkipRecaptchaParameter(isMounted),
    };

    await onPasswordLogin(email as string, passwordValue, captcha);
  };

  const onForgotPassword = (): void => {
    window.location.href = '/account/forgot-password';
  };

  if (!isMounted) {
    return <></>;
  }

  return (
    <div className={styles.continuePasswordCode}>
      {verificationCodeSent && (
        <>
          <div data-name="continuePasswordless">
            <VerificationCodeInput
              theme={theme}
              email={email}
              codeReSent={codeReSent || false}
              marketingConsent={marketingConsent}
              onEmailChange={onEmailChange}
              onResendEmail={onResendEmail}
              onVerificationCodeInputStarting={onVerificationCodeInputStarting}
              onVerificationCodeInputComplete={onVerificationCodeInputComplete}
              onVerificationCodeSubmit={onVerificationCodeSubmit}
              allowEditEmail={false}
              regUIClient={regUIClient}
            />
            <ErrorText
              errorMsg={error?.verificationCode}
              /* c8 ignore next */
              variant={theme === 'light' ? 'default' : theme}
            />
          </div>
          <OrSeparator theme={theme} />
        </>
      )}
      <div data-name="continuePassword">
        <h2
          className={classes([
            styles.header,
            { [styles.textWhite]: theme === 'dark' },
          ])}
        >
          {translateMessage({
            id: LABEL_KEYS['media-ui-login_enterYourPassword'],
            defaultMessage: defaultMessages['media-ui-login_enterYourPassword'],
          })}
        </h2>
        <div className={styles.password}>
          <InvisibleRecaptcha
            recaptchaRequired={recaptchaRequired}
            formStyles={styles.form}
            onCaptchaResponse={(captchaResponse): void =>
              void submitPassword(captchaResponse)
            }
            grecaptchaApiKey={getGrecaptchaKey()}
            formRef={recaptchaForm}
          >
            <div data-testid="wdio-password-entry">
              <OutlinedInput
                variant={theme === 'dark' ? 'dark' : 'default'}
                type="password"
                ref={passwordInputField}
                placeholder={translateMessage({
                  id: LABEL_KEYS['media-ui-login_enterYourPassword'],
                  defaultMessage:
                    defaultMessages['media-ui-login_enterYourPassword'],
                })}
                name="password"
                value={passwordValue}
                required={true}
                onChange={onChange}
                autocomplete="current-password"
                disabled={thinking}
                className={classes([
                  styles.textInput,
                  {
                    [styles.darkInput]: theme === 'dark',
                  },
                ])}
              />
              <div data-testid="wdio-password-continue-button">
                <PrimaryButton
                  /* c8 ignore next */
                  colorMode={theme === 'dark' ? 'dark' : 'light'}
                  fullWidth={true}
                  size="default"
                  type="button"
                  loading={Boolean(thinking)}
                  disabled={passwordSubmitDisabled}
                  version="phoenix"
                  onClick={(): boolean | undefined =>
                    recaptchaForm.current?.dispatchEvent(
                      /* c8 ignore next */
                      new Event('submit', { cancelable: true, bubbles: true })
                    )
                  }
                >
                  {translateMessage({
                    id: LABEL_KEYS['media-ui-login_continue'],
                    defaultMessage: defaultMessages['media-ui-login_continue'],
                  })}
                </PrimaryButton>
              </div>
            </div>
          </InvisibleRecaptcha>
          <ErrorText
            className={styles.error}
            errorMsg={error?.password}
            /* c8 ignore next */
            variant={theme === 'light' ? 'default' : 'dark'}
          />
          <div className={styles.forgotPassword}>
            <TransparentButton
              /* c8 ignore next */
              colorMode={theme === 'dark' ? 'dark' : 'light'}
              type="button"
              className={classes([styles.buttonLooksLikeALink])}
              version="phoenix"
              onClick={onForgotPassword}
            >
              {translateMessage({
                id: LABEL_KEYS['media-ui-login_forgotPassword'],
                defaultMessage:
                  defaultMessages['media-ui-login_forgotPassword'],
              })}
            </TransparentButton>
          </div>
        </div>
      </div>
    </div>
  );
};
